
<template>
  <!-- EDUCATIONAL DOCUMENTS -->
  <section id="education" class="py-5 bg-soft">
    <div class="container">
      <!-- SECTION HEADER -->
      <div class="text-center mb-5">
        <div class="education-icon mx-auto mb-3">
          <i class="bi bi-file-earmark-text-fill"></i>
        </div>

        <h2 class="textprimary fw-bold mb-2">Marketplace Resources</h2>

        <p class="text-muted mx-auto education-intro">
          Explore useful documents, product information, and resources to help
          you discover opportunities, make informed decisions, and get more
          from our marketplace.
        </p>
      </div>

      <!-- LOADING -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-success" role="status"></div>
        <p class="text-muted mt-3">Loading documents...</p>
      </div>

      <!-- ERROR -->
      <div v-else-if="error" class="alert alert-warning text-center">
        {{ error }}
        <button
          type="button"
          class="btn btn-sm btn-outline-success ms-2"
          @click="fetchDocuments"
        >
          Retry
        </button>
      </div>

      <!-- EMPTY STATE -->
      <div
        v-else-if="educationalDocuments.length === 0"
        class="text-center py-4"
      >
        <i class="bi bi-folder2-open empty-icon"></i>
        <p class="text-muted mt-3">
          No documents are available at the moment.
        </p>
      </div>

      <!-- DOCUMENTS -->
      <div v-else class="row g-4 justify-content-center">
        <div
          class="col-md-6 col-lg-4"
          v-for="document in educationalDocuments"
          :key="document.id"
        >
          <div class="document-card h-100">
            <!-- CONTENT -->
            <div class="document-content">
              <h5 class="fw-bold">
                {{ document.title }}
              </h5>

              <p class="text-muted small">
                {{ document.description || 'View this document for more information.' }}
              </p>

              <div class="document-footer">
                <span class="document-type">
                  <i class="bi bi-file-earmark-pdf-fill me-1"></i>
                  {{ document.type || 'Document' }}
                </span>

                <a
                  v-if="document.file"
                  :href="document.file"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn textprimary rounded-pill px-3"
                >
                  <i class="bi bi-eye me-1"></i>
                  View
                </a>

                <span v-else class="text-muted small">
                  File unavailable
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Config from '../components/Helpers/Config';

export default {
  data() {
    return {
      educationalDocuments: [],
      loading: false,
      error: ''
    };
  },

  mounted() {
    this.fetchDocuments();
  },

  methods: {
    async fetchDocuments() {
      this.loading = true;
      this.error = '';

      try {
        const response = await fetch(
          `${Config.API_BASE_URL}/market_docx`
        );

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const result = await response.json();

        const documents = Array.isArray(result)
          ? result
          : Array.isArray(result.data)
            ? result.data : [];

        this.educationalDocuments = documents.map((item) => {
          const file = item.doc_link ?? '';

          const title = item.title ??
            item.doc_name ??
            'Marketplace Document';

          return {
            id: item.id,
            title,
            description:
              item.description ?? '',
            file: file,
            type: item.type ?? item.document_type ?? 'Document'
          };
        });
      } catch (error) {
        console.error('Error fetching marketplace documents:', error);
        this.error = 'Unable to load documents. Please try again later.';
      } finally {
        this.loading = false;
      }
    },
  }
};
</script>

<style scoped>
.bg-soft {
  background: var(--soft-background);
}

.education-icon {
  width: 65px;
  height: 65px;
  border-radius: 50%;
  background: var(--primary-color);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.education-intro {
  max-width: 650px;
  line-height: 1.7;
}

.document-card {
  background: var(--card-background);
  border-radius: 20px;
  padding: 25px;
  display: flex;
  gap: 18px;
  border: 1px solid var(--border-color);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.document-card:hover {
  transform: translateY(-7px);
  box-shadow: 0 18px 35px rgba(0, 0, 0, 0.09);
}

.document-icon {
  width: 58px;
  height: 58px;
  min-width: 58px;
  border-radius: 15px;
  background: var(--primary-light);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.document-content {
  flex: 1;
  min-width: 0;
}

.document-content h5 {
  color: var(--text-color);
  margin-bottom: 8px;
  overflow-wrap: anywhere;
}

.document-content p {
  line-height: 1.6;
  margin-bottom: 18px;
  overflow-wrap: anywhere;
}

.document-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.document-type {
  color: var(--muted-color);
  font-size: 12px;
}

.document-type i {
  color: #dc3545;
}

.document-footer .btn {
  font-size: 13px;
  white-space: nowrap;
  transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;
}

.document-footer .btn:hover {
  transform: translateY(-2px);
}

.empty-icon {
  font-size: 42px;
  color: var(--primary-color);
}

@media (max-width: 576px) {
  .education-intro {
    font-size: 14px;
    padding: 0 10px;
  }

  .document-card {
    padding: 20px;
  }

  .document-icon {
    width: 50px;
    height: 50px;
    min-width: 50px;
    font-size: 22px;
  }

  .document-content h5 {
    font-size: 16px;
  }

  .document-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .document-footer .btn {
    width: 100%;
    text-align: center;
  }
}
</style>
